import { type FC } from 'react';

import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { _notNil } from '@/littledash';
import type { Approval } from '@/model/Approval.model';
import type { Request } from '@/model/Request.model';
import type { Study } from '@/model/Study.model';

import Approvals from '../Approvals';
import Project from '../Project';
import Reports from '../Reports';
import Requests from '../Requests';
import HealthCheckCard from './HealthCheckCard';
import InformationCard from './InformationCard';
import OverviewAccordion from './OverviewAccordion/OverviewAccordion';
import { useOverviewV2Data } from './useOverviewV2Data';

interface OverviewV2Props {
  study: Study;
  approvals?: Array<Approval>;
  requests?: Array<Request>;
}

const OverviewV2: FC<OverviewV2Props> = ({ study, approvals, requests }) => {
  const {
    information,
    informationLoading,
    overviewSections,
    overviewSectionsLoading,
    overviewSectionsFailed,
    refreshOverviewSections,
  } = useOverviewV2Data(study);
  const hasProject = _notNil(study.project_id);

  return (
    <div data-test-component="OverviewV2" data-test-element="container">
      <Tabs>
        <div className="mb4 bb b--moon-gray">
          <Tab className="f4">Overview</Tab>
          {hasProject && <Tab className="f4">Project</Tab>}
          <Tab className="f4">Randomizations</Tab>
          <Tab className="f4" counter={approvals?.length} testId="reviews-tab">
            Reviews
          </Tab>
          <Tab className="f4" counter={requests?.length} testId="requests-tab">
            Requests
          </Tab>
        </div>
        <div className="mb4">
          <Panel>
            <HealthCheckCard key={study.api_id} studyApiId={study.api_id} />
            <InformationCard information={information} loading={informationLoading} />
            <OverviewAccordion
              loading={overviewSectionsLoading}
              failed={overviewSectionsFailed}
              sections={overviewSections ?? []}
              study={study}
              studyId={String(study.id)}
              onSectionSaved={refreshOverviewSections}
              onRetry={refreshOverviewSections}
            />
          </Panel>
          {hasProject && (
            <Panel>
              <Project projectId={study.project?.id} />
            </Panel>
          )}
          <Panel>
            <Reports studyId={study.id} studyApiId={study.api_id} />
          </Panel>
          <Panel>
            <Approvals approvals={approvals} study={study} />
          </Panel>
          <Panel>
            <Requests requests={requests} study={study} />
          </Panel>
        </div>
      </Tabs>
    </div>
  );
};

export default OverviewV2;
